import moment from 'moment-timezone';

export type TCronPart = 'months' | 'years' | 'hours' | 'minutes' | 'seconds' | 'days';
export type TCronUnit =
  | 'month-days'
  | 'week-days'
  | 'months'
  | 'years'
  | 'hours'
  | 'minutes'
  | 'seconds';

export interface ICron {
  mode: string;
  value: number | number[] | string[] | undefined;
}

export interface ICronPart extends ICron {
  mode: 'every' | 'interval' | 'specific' | 'range';
}

export interface ICronDayPart extends ICron {
  mode:
    | 'every'
    | 'intervalWeekDay'
    | 'intervalMonthDay'
    | 'specificWeekDay'
    | 'specificMonthDay'
    | 'lastMonthDay'
    | 'lastMonthWeekDay'
    | 'lastWeekDay'
    | 'lastMonthDayRange'
    | 'nearestWeekDay'
    | 'firstOfMonth';
}

export type TCronPeriodOption = {
  label: string;
  value: number;
  period: string;
};

export const CRON_DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
export const CRON_MONTHS = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC'
];
const CRON_CURRENT_YEAR = moment.utc().year();
export const CRON_YEARS = Array.from({ length: 20 }, (_, i) => i + CRON_CURRENT_YEAR - 3);

export const DEFAULT_CRON = '0 0 0 ? * * *';

export const WeekDaysOptions: TCronPeriodOption[] = CRON_DAYS.map((period, i) => ({
  label: moment.utc().day(i).format('dddd'),
  value: i + 1,
  period
}));

export const MonthDaysOptions: TCronPeriodOption[] = Array.from(
  { length: 31 },
  (_, i) => i + 1
).map((day) => ({
  label: moment.utc('2000-12-31').day(day).format('Do'),
  value: day,
  period: `${day}`
}));

export const MonthOptions: TCronPeriodOption[] = CRON_MONTHS.map((period, i) => ({
  label: moment.utc().month(i).format('MMMM'),
  value: i + 1,
  period
}));

export const YearOptions: TCronPeriodOption[] = CRON_YEARS.map((period) => ({
  label: `${period}`,
  value: period,
  period: `${period}`
}));

const leftRightIsValid = (left: unknown, right: unknown) =>
  !Number.isNaN(+(left as number)) && !Number.isNaN(+(right as number));

export const cronPartToCron = (part: ICronPart) => {
  switch (part.mode) {
    case 'every': {
      return '*';
    }
    case 'interval': {
      if (Array.isArray(part.value)) {
        const [left = '1', right = '0'] = part.value;

        if (leftRightIsValid(left, right)) {
          return `${left}/${right}`;
        }
      }
      return '*';
    }
    case 'specific': {
      if (Array.isArray(part.value)) {
        return part.value.sort((a, b) => {
        if (typeof a === 'number' && typeof b === 'number') {
          return a - b;
        } else {
          return String(a).localeCompare(String(b));
        }
      }).join(',');
      }
      return '*';
    }
    case 'range': {
      if (Array.isArray(part.value)) {
        const [left = '0', right = '0'] = part.value;
        if (leftRightIsValid(left, right)) {
          return `${left}-${right}`;
        }
      }
      return '*';
    }
    default: {
      return '*';
    }
  }
};

export const secondsToCron = (seconds: ICronPart): string => cronPartToCron(seconds);
export const minutesToCron = (minutes: ICronPart): string => cronPartToCron(minutes);
export const hoursToCron = (hours: ICronPart): string => cronPartToCron(hours);
export const monthsToCron = (months: ICronPart): string => cronPartToCron(months);
export const yearsToCron = (years: ICronPart): string => cronPartToCron(years);

export const dayOfWeekToCron = (days: ICronDayPart) => {
  switch (days.mode) {
    case 'every': {
      return '*';
    }
    case 'intervalWeekDay': {
      if (Array.isArray(days.value)) {
        const [left = '1', right = '1'] = days.value;
        if (leftRightIsValid(left, right)) {
          return `${left}/${right}`;
        }
      }
      return '?';
    }
    case 'intervalMonthDay': {
      return '?';
    }
    case 'specificWeekDay': {
      if (Array.isArray(days.value)) {
        return days.value.join(',');
      }
      return '?';
    }
    case 'specificMonthDay': {
      return '?';
    }
    case 'lastMonthDay': {
      return '?';
    }
    case 'lastMonthWeekDay': {
      return '?';
    }
    case 'lastWeekDay': {
      if (Array.isArray(days.value)) {
        const [day] = days.value;
        return `${day}L`;
      }
      return '?';
    }
    case 'lastMonthDayRange': {
      return '?';
    }
    case 'nearestWeekDay': {
      return '?';
    }
    case 'firstOfMonth': {
      if (Array.isArray(days.value)) {
        const [left = '1', right = '0'] = days.value;
        if (leftRightIsValid(left, right)) {
          return `${left}#${right}`;
        }
      }
      return '?';
    }
    default: {
      return '?';
    }
  }
};

export const dayOfMonthToCron = (days: ICronDayPart) => {
  switch (days.mode) {
    case 'every': {
      return '?';
    }
    case 'intervalWeekDay': {
      return '?';
    }
    case 'intervalMonthDay': {
      if (Array.isArray(days.value)) {
        const [left = '1', right = '1'] = days.value;
        if (leftRightIsValid(left, right)) {
          return `${left}/${right}`;
        }
      }
      return '?';
    }
    case 'specificWeekDay': {
      return '?';
    }
    case 'specificMonthDay': {
      if (Array.isArray(days.value)) {
        return days.value.join(',');
      }
      return '?';
    }
    case 'lastMonthDay': {
      return 'L';
    }
    case 'lastMonthWeekDay': {
      return 'LW';
    }
    case 'lastWeekDay': {
      return '?';
    }
    case 'lastMonthDayRange': {
      if (Array.isArray(days.value)) {
        const [day = '1'] = days.value;
        return `L-${day}`;
      }
      return '?';
    }
    case 'nearestWeekDay': {
      if (Array.isArray(days.value)) {
        const [day] = days.value;
        return `${day}W`;
      }
      return '?';
    }
    case 'firstOfMonth': {
      return '?';
    }
    default: {
      return '?';
    }
  }
};

// ----------------------------------------------------------------

export const stringToCronPart = (part: string): ICronPart => {
  if (part === '*') {
    return {
      mode: 'every',
      value: undefined
    };
  }

  if (part.includes('/')) {
    const [left = 1, right = 1] = part.split('/').map(Number);
    return {
      mode: 'interval',
      value: [left, right]
    };
  }

  if (part.includes('-')) {
    const [left = 1, right = 1] = part.split('-').map(Number);
    return {
      mode: 'range',
      value: [left, right]
    };
  }

  const specifics = part.split(',').map(Number);
  if (specifics.every((s) => !Number.isNaN(s))) {
    return {
      mode: 'specific',
      value: specifics
    };
  }

  return {
    mode: 'every',
    value: undefined
  };
};

export const cronToSeconds = (seconds: string): ICronPart => stringToCronPart(seconds);
export const cronToMinutes = (minutes: string): ICronPart => stringToCronPart(minutes);
export const cronToHours = (hours: string): ICronPart => stringToCronPart(hours);
export const cronToMonths = (months: string): ICronPart => stringToCronPart(months);
export const cronToYears = (years: string): ICronPart => stringToCronPart(years);

export const cronToDays = (days: string, weekDays: string): ICronDayPart => {
  if (days === '?' && weekDays === '*') {
    return {
      mode: 'every',
      value: undefined
    };
  }

  const altWeek = weekDays.split(',');
  if (CRON_DAYS.some((d) => altWeek.includes(d))) {
    return {
      mode: 'specificWeekDay',
      value: altWeek
    };
  }

  if (days.includes('/')) {
    const [left = 1, right = 1] = days.split('/').map(Number);
    return {
      mode: 'intervalMonthDay',
      value: [left, right]
    };
  } else if (weekDays.includes('/')) {
    const [left = 1, right = 1] = weekDays.split('/').map(Number);

    return {
      mode: 'intervalWeekDay',
      value: [left, right]
    };
  }

  if (days === 'L') {
    return {
      mode: 'lastMonthDay',
      value: undefined
    };
  }

  if (days === 'LW') {
    return {
      mode: 'lastMonthWeekDay',
      value: undefined
    };
  }

  if (days.startsWith('L-')) {
    const day = days.split('L-')[1];
    if (day) {
      return {
        mode: 'lastMonthDayRange',
        value: [+day]
      };
    }
  }

  if (weekDays.endsWith('L') && weekDays.length === 2) {
    const day = weekDays[0]!;

    return {
      mode: 'lastWeekDay',
      value: [+day]
    };
  }

  if (days.endsWith('W')) {
    const day = days.split('W')[0]!;

    return {
      mode: 'nearestWeekDay',
      value: [+day]
    };
  }

  if (weekDays.includes('#')) {
    const [left = 1, right = 1] = weekDays.split('#').map(Number);
    return {
      mode: 'firstOfMonth',
      value: [left, right]
    };
  }

  const altMonth = days.split(',').map(Number);
  if (altMonth.every((m) => !Number.isNaN(m))) {
    return {
      mode: 'specificMonthDay',
      value: altMonth
    };
  }

  return {
    mode: 'every',
    value: undefined
  };
};

export const defaultByMode = (minLeft: number = 1, minRight: number = 1) => ({
  every: undefined,
  interval: [minLeft, minRight],
  specific: [minLeft],
  range: [minLeft, minRight]
});

export const CRON_RADIO_OPTIONS = [
  { label: 'cronEditor.every', value: 'every' },
  { label: 'cronEditor.interval', value: 'interval' },
  { label: 'cronEditor.specific', value: 'specific' },
  { label: 'cronEditor.range', value: 'range' }
];

export const CRON_DAY_RADIO_OPTIONS = [
  { label: 'cronEditor.days.every', value: 'every', isDayWeek: true, isDayMonth: true },
  {
    label: 'cronEditor.days.intervalWeekDay',
    value: 'intervalWeekDay',
    isDayWeek: true,
    isDayMonth: false
  },
  {
    label: 'cronEditor.days.intervalMonthDay',
    value: 'intervalMonthDay',
    isDayWeek: false,
    isDayMonth: true
  },
  {
    label: 'cronEditor.days.specificWeekDay',
    value: 'specificWeekDay',
    isDayWeek: true,
    isDayMonth: false
  },
  {
    label: 'cronEditor.days.specificMonthDay',
    value: 'specificMonthDay',
    isDayWeek: false,
    isDayMonth: true
  },
  {
    label: 'cronEditor.days.lastMonthDay',
    value: 'lastMonthDay',
    isDayWeek: false,
    isDayMonth: true
  },
  {
    label: 'cronEditor.days.lastMonthWeekDay',
    value: 'lastMonthWeekDay',
    isDayWeek: false,
    isDayMonth: true
  },
  {
    label: 'cronEditor.days.lastWeekDay',
    value: 'lastWeekDay',
    isDayWeek: true,
    isDayMonth: false
  },
  {
    label: 'cronEditor.days.lastMonthDayRange',
    value: 'lastMonthDayRange',
    isDayWeek: false,
    isDayMonth: true
  },
  {
    label: 'cronEditor.days.nearestWeekDay',
    value: 'nearestWeekDay',
    isDayWeek: false,
    isDayMonth: true
  },
  {
    label: 'cronEditor.days.firstOfMonth',
    value: 'firstOfMonth',
    isDayWeek: true,
    isDayMonth: false
  }
];
